/**
 * Centralized SWR cache key registry.
 *
 * All useSWR calls and mutate() calls should reference these constants
 * instead of inline strings to prevent typos and make key usage greppable.
 *
 * For dynamic keys (e.g. per-ID endpoints), use the builder functions.
 */
export const SWR_KEYS = {
  // ── User ──────────────────────────────────────────────────────────────────
  me: "/api/me",

  // ── Health ────────────────────────────────────────────────────────────────
  health: "/api/health",

  // ── Settings ──────────────────────────────────────────────────────────────
  settings: "/api/settings",
  enterpriseSettings: "/api/enterprise-settings",
  customAnalyticsScript: "/api/enterprise-settings/custom-analytics-script",
  authType: "/api/auth/type",

  // ── Agents / Personas ─────────────────────────────────────────────────────
  personas: "/api/persona",
  persona: (id: number) => `/api/persona/${id}`,
  agentPreferences: "/api/user/assistant/preferences",
  defaultAssistantConfig: "/api/admin/default-assistant/configuration",
  personaLabels: "/api/persona/labels",
  // TASK-EMAIL-011 (SOL-EMAIL-005) — persona-scoped mail.* tool grant.
  // Path CONFIRMED against the real TASK-EMAIL-010 backend (landed in this
  // same working tree while this file was written — see
  // backend/services/ai-service/cmd/server/main.go:1038 and
  // internal/presentation/http/handler/persona_handler.go's SetMailGrant):
  // `PUT /persona/:id/mail-grant`, proxied by api-gateway's EXISTING generic
  // `/api/persona` → ai-service `/persona` route (router.go:1894) — no new
  // gateway route needed. NOT `/api/agents/:id/mail-grant` as this task's
  // own spec doc sketched.
  //
  // FIXED (follow-up): GET /persona/:id/mail-grant now also registered
  // (persona_handler.go's GetMailGrant) — the toggle reads the real
  // persisted value. useAgentMailGrant.ts's fetcher still degrades a
  // missing/erroring GET to "unknown → false" defensively.
  agentMailGrant: (agentId: number) => `/api/persona/${agentId}/mail-grant`,

  // ── LLM Providers ─────────────────────────────────────────────────────────
  llmProviders: "/api/llm/provider",
  llmProvidersForPersona: (personaId: number) =>
    `/api/llm/persona/${personaId}/providers`,
  adminLlmProviders: "/api/admin/llm/provider",
  llmProvidersWithImageGen: "/api/admin/llm/provider?include_image_gen=true",
  customProviderNames: "/api/admin/llm/custom-provider-names",
  wellKnownLlmProviders: "/api/admin/llm/built-in/options",
  wellKnownLlmProvider: (providerEndpoint: string) =>
    `/api/admin/llm/built-in/options/${providerEndpoint}`,

  // ── Image Generation ──────────────────────────────────────────────────────
  imageGenConfig: "/api/admin/image-generation/config",

  // ── Documents ─────────────────────────────────────────────────────────────
  documentSets: "/api/manage/document-set",
  documentSetsEditable: "/api/manage/document-set?get_editable=true",
  tags: "/api/query/valid-tags",
  connectorStatus: "/api/manage/connector-status",

  // ── Credentials & Connectors ──────────────────────────────────────────────
  adminCredentials: "/api/manage/admin/credential",
  indexingStatus: "/api/manage/admin/connector/indexing-status",
  adminConnectorStatus: "/api/manage/admin/connector/status",
  adminConnectors: "/api/manage/admin/connector",
  federatedConnectors: "/api/federated",

  // ── Google Connectors ─────────────────────────────────────────────────────
  googleConnectorAppCredential: (service: "gmail" | "google-drive") =>
    `/api/manage/admin/connector/${service}/app-credential`,
  googleConnectorServiceAccountKey: (service: "gmail" | "google-drive") =>
    `/api/manage/admin/connector/${service}/service-account-key`,
  googleConnectorCredentials: (service: "gmail" | "google-drive") =>
    `/api/manage/admin/connector/${service}/credentials`,
  googleConnectorPublicCredential: (service: "gmail" | "google-drive") =>
    `/api/manage/admin/connector/${service}/public-credential`,
  googleConnectorServiceAccountCredential: (
    service: "gmail" | "google-drive"
  ) => `/api/manage/admin/connector/${service}/service-account-credential`,

  // ── Search Settings ───────────────────────────────────────────────────────
  currentSearchSettings: "/api/search-settings/get-current-search-settings",
  secondarySearchSettings: "/api/search-settings/get-secondary-search-settings",

  // ── Chat Sessions ─────────────────────────────────────────────────────────
  chatSessions: "/api/chat/get-user-chat-sessions",

  // ── Projects & Files ──────────────────────────────────────────────────────
  userProjects: "/api/user/projects",
  recentFiles: "/api/user/files/recent",
  userPats: "/api/user/pats",
  notifications: "/api/notifications",

  // ── Users ─────────────────────────────────────────────────────────────────
  acceptedUsers: "/api/manage/users/accepted/all",
  invitedUsers: "/api/manage/users/invited",
  // Curator-accessible listing of all users (and optionally service-account
  // entries when `?include_api_keys=true`). Used by group create/edit pages so
  // global curators — who cannot hit the admin-only `/accepted/all` and
  // `/invited` endpoints — can still load the member picker.
  groupMemberCandidates: "/api/manage/users?include_api_keys=true",
  pendingTenantUsers: "/api/tenants/users/pending",
  userCounts: "/api/manage/users/counts",

  // ── API Keys ──────────────────────────────────────────────────────────────
  adminApiKeys: "/api/admin/api-key",

  // ── Groups ────────────────────────────────────────────────────────────────
  adminUserGroups: "/api/manage/admin/user-group",
  shareableGroups: "/api/manage/user-groups/minimal",
  // CR-020 — replaces the old "scimToken" key, which pointed at a
  // non-existent endpoint (fell through to the enterprise-settings wildcard).
  scimTokenStatus: "/api/v1/admin/scim/token/status",

  // ── MCP Servers ───────────────────────────────────────────────────────────
  adminMcpServers: "/api/admin/mcp/servers",
  mcpServers: "/api/mcp/servers",
  // CR-MCPPERS-001 — per-user personal MCP servers (Enterprise Edition).
  personalMcpServers: "/api/mcp/personal-servers",

  // ── Tools ─────────────────────────────────────────────────────────────────
  tools: "/api/tool",
  openApiTools: "/api/tool/openapi",
  oauthTokenStatus: "/api/user-oauth-token/status",

  // ── Voice ─────────────────────────────────────────────────────────────────
  voiceProviders: "/api/admin/voice/providers",
  voiceStatus: "/api/voice/status",

  // ── Build (Craft) ─────────────────────────────────────────────────────────
  buildConnectors: "/api/build/connectors",
  buildUserLibraryTree: "/api/build/user-library/tree",
  buildSessionFiles: (sessionId: string) =>
    `/api/build/sessions/${sessionId}/files?path=`,
  buildSessionOutputFiles: (sessionId: string) =>
    `/api/build/sessions/${sessionId}/files?path=outputs`,
  buildSessionWebappInfo: (sessionId: string) =>
    `/api/build/sessions/${sessionId}/webapp-info`,
  buildSessionArtifacts: (sessionId: string) =>
    `/api/build/sessions/${sessionId}/artifacts`,
  buildSessionArtifactFile: (sessionId: string, filePath: string) =>
    `/api/build/sessions/${sessionId}/artifacts/${filePath}`,
  buildSessionPptxPreview: (sessionId: string, filePath: string) =>
    `/api/build/sessions/${sessionId}/pptx-preview/${filePath}`,

  // ── OpenSearch Migration ──────────────────────────────────────────────────
  opensearchMigrationStatus: "/api/admin/opensearch-migration/status",
  opensearchMigrationRetrieval: "/api/admin/opensearch-migration/retrieval",

  // ── Token Rate Limits ─────────────────────────────────────────────────────
  globalTokenRateLimits: "/api/admin/token-rate-limits/global",
  userTokenRateLimits: "/api/admin/token-rate-limits/users",
  userGroupTokenRateLimits: "/api/admin/token-rate-limits/user-groups",
  userGroupTokenRateLimit: (groupId: number) =>
    `/api/admin/token-rate-limits/user-group/${groupId}`,

  // ── Google API Quota (CR-017) ────────────────────────────────────────────
  googleQuotaUsage: "/api/v1/admin/quota/google-usage",

  // ── Google API Quota Override per Workspace (CR-044) ─────────────────────
  googleQuotaOverrides: "/api/v1/admin/quota/overrides",
  googleQuotaOverride: (apiKind: string) =>
    `/api/v1/admin/quota/overrides/${apiKind}`,

  // ── Usage Reports ─────────────────────────────────────────────────────────
  usageReport: "/api/admin/usage-report",

  // ── Web Search ────────────────────────────────────────────────────────────
  webSearchContentProviders: "/api/admin/web-search/content-providers",
  webSearchSearchProviders: "/api/admin/web-search/search-providers",

  // ── Prompt shortcuts ──────────────────────────────────────────────────────
  promptShortcuts: "/api/input_prompt",

  // ── License & Billing ─────────────────────────────────────────────────────
  license: "/api/license",
  billingInformationCloud: "/api/tenants/billing-information",
  billingInformationSelfHosted: "/api/admin/billing/billing-information",

  // ── Admin ─────────────────────────────────────────────────────────────────
  hooks: "/api/admin/hooks",
  hookSpecs: "/api/admin/hooks/specs",

  // ── Standard Answers (EE) ─────────────────────────────────────────────────
  standardAnswerCategories: "/api/manage/admin/standard-answer/category",
  standardAnswers: "/api/manage/admin/standard-answer",

  // ── Query History (EE) ────────────────────────────────────────────────────
  adminChatSessionHistory: "/api/admin/chat-session-history",
  adminChatSession: (id: string) => `/api/admin/chat-session-history/${id}`,

  // ── MCP Server (per-ID) ───────────────────────────────────────────────────
  adminMcpServer: (id: number) => `/api/admin/mcp/servers/${id}`,

  // ── Document Processing ───────────────────────────────────────────────────
  unstructuredApiKeySet: "/api/search-settings/unstructured-api-key-set",

  // ── Connectors ────────────────────────────────────────────────────────────
  connector: "/api/manage/connector",

  // ── Audit Log (CR-016) ───────────────────────────────────────────────────
  // Full path (not "/api/manage/..." like most keys above) — backend SOL-016
  // §4.1 places this under the newer "/api/v1/admin" gateway group. NOTE: at
  // implementation time this exact path does not exist on the backend yet —
  // only "/api/v1/admin/audit" (no "-log") exists, as a fixed stub. See
  // ui/specs/crs/v1/audit-log-viewer/tasks/TASK-CR-016-002-*.md "Kết quả
  // thực thi" for the verified backend state.
  auditLog: (params: string) => `/api/v1/admin/audit-log?${params}`,
  auditLogEntry: (id: string) => `/api/v1/admin/audit-log/${id}`,

  // ── Knowledge Query API (CR-013) ──────────────────────────────────────────
  knowledgeSources: "/api/v1/knowledge/sources",

  // ── Tool Approvals (TASK-DOCGOV-023, v8/doc-governance) ───────────────────
  // Proxied through api-gateway's registerFrontendProxy to mcp-proxy's
  // POST /api/v1/admin/tool-approvals — see api-gateway router.go's
  // adminMCPProxyGroup block.
  toolApprovals: "/api/admin/mcp-proxy/tool-approvals",

  // ── Agent Peer Grants (TASK-DOCAGENT-011, v8/doc-agent) ───────────────────
  // Same mcp-proxy admin proxy group as toolApprovals above — see
  // src/lib/agent-peer-grants/svc.ts's CREDENTIALS_URL/GRANTS_URL (the real
  // backend paths this key must match, since useAgentPeerGrants/
  // useAgentPeerCredentials fetch directly against it, not just use it as a
  // cache key).
  agentPeerCredentials: "/api/admin/mcp-proxy/agent-peer-credentials",
  agentPeerGrants: "/api/admin/mcp-proxy/agent-peer-grants",
} as const;
